/**
 * CapabilitiesPanel — the effective capability set (server-authoritative: preset ∪ grants − denies,
 * deny-wins). Read-only view of what THIS session may do. A live "refresh" re-pulls the session seam
 * via the store's refreshCapabilities (e.g. after an admin grant/deny lands). Empty set is a
 * first-class state (deny-by-default), not an error — surfaced via the Table's EmptyState wiring.
 */
import { useState } from "react";
import { Button, Card, Table, type Column } from "@/ui";
import { ApiError } from "@/api";
import { useToast } from "@/ui";
import { useT } from "@/app";
import { useRefreshSession } from "../useSession";

interface CapRow {
  cap: string;
}

export interface CapabilitiesPanelProps {
  capabilities: ReadonlyArray<string>;
}

export function CapabilitiesPanel({ capabilities }: CapabilitiesPanelProps) {
  const { t } = useT();
  const toast = useToast();
  const refresh = useRefreshSession();
  const [refreshing, setRefreshing] = useState(false);

  const rows: CapRow[] = capabilities.map((cap) => ({ cap }));

  const columns: ReadonlyArray<Column<CapRow>> = [
    {
      key: "cap",
      header: t("auth.session.caps.col"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.cap,
      cell: (r) => <span className="nv-ltr-island">{r.cap}</span>,
    },
  ];

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await refresh();
      toast.success(t("auth.session.caps.refreshed"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("auth.session.caps.refresh.error"));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Card
      title={t("auth.session.caps.title")}
      actions={
        <Button variant="secondary" size="sm" loading={refreshing} onClick={onRefresh}>
          {t("auth.session.caps.refresh")}
        </Button>
      }
      padded={false}
    >
      <div style={{ padding: "var(--nv-s-3) var(--nv-s-4)", color: "var(--nv-slate)" }}>
        {t("auth.session.caps.count", { n: capabilities.length })}
      </div>
      <Table<CapRow>
        columns={columns}
        rows={rows}
        rowKey={(r) => r.cap}
        caption={t("auth.session.caps.title")}
        emptyTitle={t("auth.session.caps.empty.title")}
        emptyDescription={t("auth.session.caps.empty.desc")}
        pageSize={12}
      />
    </Card>
  );
}
